import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import { aiThumbnailService } from '../../services/aiThumbnailService';
import { videoRecorderService } from '../../services/videoRecorderService';
import { VideoRecording } from '../../types/videoRecorder';

interface Props {
  open: boolean;
  onClose: () => void;
  video: VideoRecording;
  onApplied: (updated: { thumbnailPath: string; signedUrl?: string }) => void;
}

const AIThumbnailModal: React.FC<Props> = ({ open, onClose, video, onApplied }) => {
  const [description, setDescription] = useState<string>('Clean, high-contrast YouTube-style thumbnail that highlights the key idea');
  const [loading, setLoading] = useState<boolean>(false);
  const [options, setOptions] = useState<Array<{ id: string; url: string }>>([]);

  if (!open) return null;

  const handleGenerate = async () => {
    try {
      if (!description.trim()) {
        toast.error('Please enter a description');
        return;
      }
      setLoading(true);
      const imgs = await aiThumbnailService.generate({ description, title: video.title, count: 4 });
      setOptions(imgs);
      if (imgs.length === 0) toast.error('No images generated');
    } catch (e) {
      console.error(e);
      toast.error('Failed to generate thumbnails');
    } finally {
      setLoading(false);
    }
  };

  const handleUse = async (url: string) => {
    try {
      setLoading(true);
      const path = await videoRecorderService.uploadThumbnailFromUrl(video.id, url);
      if (!path) throw new Error('Upload failed');
      const signed = await videoRecorderService.getThumbnailSignedUrl(path, 3600);
      onApplied({ thumbnailPath: path, signedUrl: signed || undefined });
      toast.success('Thumbnail applied');
      onClose();
    } catch (e) {
      console.error(e);
      toast.error('Failed to apply thumbnail');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white w-full max-w-2xl rounded-lg shadow-lg p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold">Generate AI Thumbnail</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">✕</button>
        </div>
        <label className="block text-sm font-medium mb-1">Description</label>
        <textarea
          className="w-full border rounded p-2 text-sm"
          rows={3}
          placeholder={`Describe the thumbnail for: ${video.title}`}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
        <div className="mt-3 flex gap-2">
          <button
            onClick={handleGenerate}
            disabled={loading}
            className="px-3 py-2 bg-indigo-600 text-white rounded disabled:opacity-60"
          >
            {loading ? 'Generating…' : 'Generate'}
          </button>
          <button onClick={onClose} className="px-3 py-2 border rounded">Cancel</button>
        </div>

        {options.length > 0 && (
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
            {options.map((opt) => (
              <div key={opt.id} className="border rounded overflow-hidden">
                <img src={opt.url} alt="AI option" className="w-full h-28 object-cover" />
                <button
                  onClick={() => handleUse(opt.url)}
                  disabled={loading}
                  className="w-full py-1.5 text-sm bg-gray-900 text-white hover:bg-black disabled:opacity-60"
                >
                  Use this
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AIThumbnailModal;
